// Logique pure des écrans du budget et de la dépense (lot L4-B) : montants en francs CFA, tons des
// états, étape d'un engagement, regroupement des lignes par section et sens, contrôle d'une décision
// modificative saisie, mention « (fictif) » d'un budget de présentation. Testée seule.
import { ETAPES_DEPENSE, ETATS_BESOIN, ETATS_BUDGET, ETATS_ENGAGEMENT, SECTIONS_BUDGET, SENS_LIGNE_BUDGET, type EtapeDepense, type EtatBesoin, type EtatBudget, type EtatEngagement, type LigneBudgetaire, type SectionBudget, type SensLigneBudget } from '@cc/contrats';
import type { TonEtat } from '@cc/ui';

/** « 1 500 000 F » ; « (fictif) » ajouté pour un budget de présentation. */
export function francs(n: number, fictif = false): string {
  return `${n.toLocaleString('fr-FR')} F${fictif ? ' (fictif)' : ''}`;
}

export const TON_BUDGET: Record<EtatBudget, TonEtat> = { en_preparation: 'attente', propose: 'attente', adopte: 'ok', executoire: 'ok', clos: 'neutre' };
export const TON_BESOIN: Record<EtatBesoin, TonEtat> = { a_valider: 'attente', valide: 'ok', refuse: 'retard', engage: 'ok', annule: 'neutre' };
export const TON_ENGAGEMENT: Record<EtatEngagement, TonEtat> = { engage: 'ok', refuse: 'retard', annule: 'neutre' };

export const libelleEtatBudget = (e: EtatBudget) => ETATS_BUDGET[e];
export const libelleEtatBesoin = (e: EtatBesoin) => ETATS_BESOIN[e];
export const libelleEtatEngagement = (e: EtatEngagement) => ETATS_ENGAGEMENT[e];
export const libelleEtape = (e: EtapeDepense) => ETAPES_DEPENSE[e];

/** Ordre des étapes de la chaîne, pour la barre de progression d'un engagement. */
export const ORDRE_ETAPES: EtapeDepense[] = ['besoin', 'validation', 'engagement', 'service_fait', 'liquidation', 'ordonnancement', 'paiement'];
export function rangEtape(e: EtapeDepense): number {
  return ORDRE_ETAPES.indexOf(e);
}

export interface GroupeLignes {
  section: SectionBudget;
  sens: SensLigneBudget;
  titre: string;
  lignes: LigneBudgetaire[];
}

/** Lignes groupées par section puis par sens (dépenses avant recettes). */
export function grouperLignes(lignes: LigneBudgetaire[]): GroupeLignes[] {
  const groupes: GroupeLignes[] = [];
  for (const section of ['fonctionnement', 'investissement'] as SectionBudget[]) {
    for (const sens of ['depense', 'recette'] as SensLigneBudget[]) {
      const l = lignes.filter((x) => x.section === section && x.sens === sens);
      if (l.length > 0) groupes.push({ section, sens, titre: `${SECTIONS_BUDGET[section]} · ${SENS_LIGNE_BUDGET[sens]}`, lignes: l });
    }
  }
  return groupes;
}

/** Taux d'exécution (ordonnancé / crédits ouverts) en pourcentage à une décimale, ou null. */
export function tauxExecution(m: { ordonnance: number; credits_ouverts: number }): number | null {
  if (m.credits_ouverts <= 0) return null;
  return Math.round((1000 * m.ordonnance) / m.credits_ouverts) / 10;
}

export interface MouvementSaisi {
  ligne_id: string;
  montant: string;
}

/** Contrôle d'une décision modificative saisie : montants entiers non nuls, lignes distinctes ; total. */
export function controlerMouvements(m: MouvementSaisi[]): { erreurs: string[]; total: number; mouvements: { ligne_id: string; montant: number }[] } {
  const erreurs: string[] = [];
  const vus = new Set<string>();
  const mouvements: { ligne_id: string; montant: number }[] = [];
  m.forEach((x, i) => {
    const montant = Number(x.montant.replace(/\s/g, ''));
    if (!x.ligne_id) erreurs.push(`Mouvement ${i + 1} : choisir une ligne`);
    else if (vus.has(x.ligne_id)) erreurs.push(`Mouvement ${i + 1} : ligne déjà utilisée`);
    if (!Number.isInteger(montant) || montant === 0) erreurs.push(`Mouvement ${i + 1} : montant entier non nul (négatif pour une réduction)`);
    if (x.ligne_id) vus.add(x.ligne_id);
    mouvements.push({ ligne_id: x.ligne_id, montant: Number.isFinite(montant) ? montant : 0 });
  });
  if (m.length === 0) erreurs.push('Au moins un mouvement');
  return { erreurs, total: mouvements.reduce((s, x) => s + x.montant, 0), mouvements };
}

export function aujourdhuiIso(maintenant = new Date()): string {
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}
